/**
 * `crypto.randomUUID` is gated on a secure context. `https://`, `localhost` and
 * `127.0.0.1` have it; a phone pointed at `http://<lan-ip>:5173`, an Android
 * WebView and older Safari builds do not — there the property is simply absent
 * and calling it throws.
 *
 * That matters more than it looks: minting the device uuid is the first await
 * in both apps' bootstrap (D-06 — a login without one can only be refused), so
 * a throw here is a blank screen, not a degraded one.
 *
 * `getRandomValues` carries no such requirement, so a v4 is assembled from it
 * wherever `randomUUID` is missing.
 */
export function randomUuidV4(): string {
  const source = globalThis.crypto;
  if (typeof source?.randomUUID === 'function') return source.randomUUID();

  const bytes = new Uint8Array(16);
  source.getRandomValues(bytes);
  const at = (index: number): number => bytes[index] ?? 0;
  const version = (at(6) & 0x0f) | 0x40;
  const variant = (at(8) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte, index) => {
    const value = index === 6 ? version : index === 8 ? variant : byte;
    return value.toString(16).padStart(2, '0');
  }).join('');

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}
